import { useEffect, useState, useCallback } from "react";
import { Wallet, Loader2, Download, FileText, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const money = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const monthStart = () => new Date().toISOString().slice(0, 8) + "01";
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const AGING = [
  ["d0_30", "0–30d", "bg-emerald-50 text-emerald-700"],
  ["d30_60", "30–60d", "bg-amber-50 text-amber-700"],
  ["d60_plus", "60+d", "bg-rose-50 text-rose-700"],
];

function AgingChips({ aging, testid }) {
  const chips = AGING.filter(([k]) => Math.round(aging?.[k] || 0) > 0);
  if (chips.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap justify-end gap-1" data-testid={testid}>
      {chips.map(([k, label, cls]) => (
        <span key={k} className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`${testid}-${k}`}>
          {label} {money(aging[k])}
        </span>
      ))}
    </div>
  );
}

export default function AdminPayoutsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const params = useCallback(() => {
    const p = {};
    if (start) p.start = start;
    if (end) p.end = end;
    if (sellerId) p.seller_id = sellerId;
    return p;
  }, [start, end, sellerId]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/marketplace/admin/payouts-report", { params: params() })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], totals: {}, sellers: [] }))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const setThisMonth = () => { setStart(monthStart()); setEnd(today()); };
  const clearRange = () => { setStart(""); setEnd(""); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/marketplace/admin/payouts-report/export", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `payouts_${start || "all"}_${end || "now"}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed — please try again"); } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const r = await api.get("/marketplace/admin/payouts-report/pdf", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `payouts_statement_${start || "all"}_${end || "now"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF statement downloaded");
    } catch { toast.error("PDF export failed — please try again"); } finally { setExportingPdf(false); }
  };

  const openDrill = async (row) => {
    setDrill({ seller_name: row.seller_name, seller_id: row.seller_id });
    setDrillLoading(true);
    try {
      const p = {};
      if (start) p.start = start;
      if (end) p.end = end;
      const r = await api.get(`/marketplace/admin/payouts-report/seller/${row.seller_id}`, { params: p });
      setDrill(r.data);
    } catch { toast.error("Could not load seller details"); setDrill(null); }
    finally { setDrillLoading(false); }
  };

  const [sellerExporting, setSellerExporting] = useState("");
  const exportSeller = async (kind) => {
    if (!drill?.seller_id) return;
    setSellerExporting(kind);
    try {
      const p = { seller_id: drill.seller_id };
      if (start) p.start = start;
      if (end) p.end = end;
      const path = kind === "pdf" ? "/marketplace/admin/payouts-report/pdf" : "/marketplace/admin/payouts-report/export";
      const type = kind === "pdf" ? "application/pdf" : "text/csv";
      const r = await api.get(path, { params: p, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type }));
      const a = document.createElement("a");
      const safe = (drill.seller_name || "seller").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      a.href = url; a.download = `payouts_${safe}_${start || "all"}_${end || "now"}.${kind === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Seller ${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed — please try again"); } finally { setSellerExporting(""); }
  };

  const t = data?.totals || {};
  const rows = data?.rows || [];
  const sellers = data?.sellers || [];

  return (
    <div data-testid="admin-payouts-report-page">
      <PageHeader title="Payouts Report" icon={Wallet} subtitle="Total sales per seller minus our marketplace commission. Delivered/completed orders and service bookings — real ledger figures." />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="payouts-filters">
        <label className="text-xs font-semibold text-slate-500">From
          <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} data-testid="payout-start" className="mt-1 block h-9 rounded-lg border border-slate-200 px-2 text-sm" />
        </label>
        <label className="text-xs font-semibold text-slate-500">To
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="payout-end" className="mt-1 block h-9 rounded-lg border border-slate-200 px-2 text-sm" />
        </label>
        <label className="text-xs font-semibold text-slate-500">Seller
          <select value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="payout-seller" className="mt-1 block h-9 min-w-[180px] rounded-lg border border-slate-200 px-2 text-sm">
            <option value="">All sellers</option>
            {sellers.map((s) => <option key={s.seller_id} value={s.seller_id}>{s.seller_name}</option>)}
          </select>
        </label>
        <button onClick={setThisMonth} data-testid="payout-this-month" className="h-9 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50">This month</button>
        <button onClick={clearRange} data-testid="payout-clear" className="h-9 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50">All time</button>
        <button onClick={exportCsv} disabled={exporting || rows.length === 0} data-testid="payout-export-csv" className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
        </button>
        <button onClick={exportPdf} disabled={exportingPdf || rows.length === 0} data-testid="payout-export-pdf" className="inline-flex h-9 items-center gap-1.5 rounded-full brand-gradient-bg px-4 text-sm font-semibold text-white disabled:opacity-50">
          {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Export PDF
        </button>
      </div>

      {loading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[["Gross sales", t.gross, "text-slate-900"], ["Our commission", t.commission, "text-rose-600"], ["Seller net", t.net, "text-emerald-600"], ["Paid out", t.paid_out, "text-blue-600"], ["Outstanding", t.outstanding, "text-amber-600"]].map(([k, v, c]) => (
              <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`payout-total-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className={`text-xl font-extrabold ${c}`}>{money(v)}</p>
                <p className="text-xs text-slate-500">{k}</p>
              </div>
            ))}
          </div>

          {Math.round(t.outstanding || 0) > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-sm" data-testid="payout-aging-summary">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Outstanding aging</span>
              {AGING.map(([k, label, cls]) => (
                <span key={k} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`payout-aging-total-${k}`}>
                  {label}: {money(t.aging?.[k])}
                </span>
              ))}
            </div>
          )}

          {rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="payouts-empty">
              <Wallet className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-700">No completed sales in this view</p>
              <p className="mt-1 text-sm text-slate-500">Try a wider date range or a different seller.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="payouts-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="p-3">Seller</th>
                    <th className="p-3 text-right">Orders</th>
                    <th className="p-3 text-right">Gross sales</th>
                    <th className="p-3 text-right">Commission</th>
                    <th className="p-3 text-right">Net payable</th>
                    <th className="p-3 text-right">Paid out</th>
                    <th className="p-3 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.seller_id} onClick={() => openDrill(r)} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50" data-testid={`payout-row-${r.seller_id}`}>
                      <td className="p-3 font-medium text-fuchsia-700 hover:underline">{r.seller_name}</td>
                      <td className="p-3 text-right text-slate-600">{r.orders}</td>
                      <td className="p-3 text-right text-slate-800">{money(r.gross)}</td>
                      <td className="p-3 text-right text-rose-600">−{money(r.commission)}</td>
                      <td className="p-3 text-right font-semibold text-emerald-700">{money(r.net)}</td>
                      <td className="p-3 text-right text-blue-600">{money(r.paid_out)}</td>
                      <td className="p-3 text-right align-top">
                        <span className="font-semibold text-amber-600">{money(r.outstanding)}</span>
                        <AgingChips aging={r.aging} testid={`payout-aging-${r.seller_id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drill && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="payout-drilldown">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDrill(null)} data-testid="payout-drilldown-backdrop" />
          <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-600">Seller breakdown</p>
                <h3 className="mt-1 text-lg font-bold text-slate-900" data-testid="payout-drilldown-name">{drill.seller_name}</h3>
                <p className="text-xs text-slate-500">{(start || end) ? `${start || "all"} → ${end || "now"}` : "All time"}</p>
              </div>
              <button onClick={() => setDrill(null)} data-testid="payout-drilldown-close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>

            {drillLoading ? (
              <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 border-b border-slate-100 p-5">
                  <div><p className="text-lg font-extrabold text-slate-900">{money(drill.totals?.gross)}</p><p className="text-xs text-slate-500">Gross</p></div>
                  <div><p className="text-lg font-extrabold text-rose-600">−{money(drill.totals?.commission)}</p><p className="text-xs text-slate-500">Commission</p></div>
                  <div><p className="text-lg font-extrabold text-emerald-700">{money(drill.totals?.net)}</p><p className="text-xs text-slate-500">Net payable</p></div>
                </div>
                <div className="flex gap-2 border-b border-slate-100 px-5 py-3">
                  <button onClick={() => exportSeller("pdf")} disabled={!!sellerExporting || (drill.items || []).length === 0} data-testid="payout-seller-export-pdf" className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full brand-gradient-bg px-3 text-sm font-semibold text-white disabled:opacity-50">
                    {sellerExporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Statement PDF
                  </button>
                  <button onClick={() => exportSeller("csv")} disabled={!!sellerExporting || (drill.items || []).length === 0} data-testid="payout-seller-export-csv" className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    {sellerExporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  {(drill.items || []).length === 0 ? (
                    <p className="py-10 text-center text-sm text-slate-500" data-testid="payout-drilldown-empty">No orders or bookings in this view.</p>
                  ) : (
                    <ul className="space-y-2">
                      {drill.items.map((it, i) => (
                        <li key={i} className="flex items-center justify-between rounded-xl border border-slate-100 p-3" data-testid={`payout-drill-item-${i}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${it.type === "booking" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-600"}`}>{it.type}</span>
                              <span className="truncate font-semibold text-slate-800">{it.ref}</span>
                            </div>
                            <p className="mt-0.5 truncate text-xs text-slate-500">{it.label ? `${it.label} · ` : ""}{fmtDate(it.date)} · {it.status}</p>
                          </div>
                          <div className="ml-3 text-right">
                            <p className="font-semibold text-slate-900">{money(it.gross)}</p>
                            <p className="text-xs text-emerald-700">net {money(it.net)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
